import { sortBy } from "lodash";
import {
    Kudos,
    KudosRecipients,
    Mood,
    MoodValue,
    Reactions, Threads,
    TimelineItem,
    TimelineItemType,
    Topic
} from "@teammood/timeline";
import { Injectable } from "@angular/core";
import { LoggerService } from "@teammood/shared/util/logger";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { TaskActionContentDTO } from "../../taskbook-triage/taskbook.types";

@Injectable({
    providedIn: 'root'
})
export class DaysService {

    constructor(
        private log: LoggerService,
        private http: HttpClient,
    ) {}


    async createMood(moodValue: MoodValue, teamId: string) {
        const itemType = "moodItem"
        try {
            return await firstValueFrom(this.http.post(`/api/timeline/new/${itemType}/${teamId}`, {mood: moodValue, teamId: teamId}));
        }
        catch (error) {
            this.log.error(error.data || error);
        }
    }

    async updateMood(mood: Mood) {
        const itemType = "moodItem"
        try {
            return await firstValueFrom(this.http.post(`/api/timeline/${itemType}/${mood.id}`, {mood: mood}));
        }
        catch (error) {
            this.log.error(error.data || error);
        }
    }

    async updateTimelineItem(timelineItem: TimelineItem, itemType: TimelineItemType) {
        try {
            return await firstValueFrom(this.http.post(`/api/timeline/${itemType}/${timelineItem.id}`, timelineItem));
        }
        catch (error) {
            this.log.error(error.data || error);
        }
    }

    async deleteTimelineItem(timelineItem: TimelineItem) {
        try {
            return await firstValueFrom(this.http.delete(`/api/timeline/${timelineItem.itemType}/${timelineItem.id}`));
        }
        catch (error) {
            this.log.error(error.data || error);
        }
    }

    async moderateTimelineItem(timelineItem: TimelineItem, reason: string) {
        try {
            return await firstValueFrom(this.http.put(`/api/timeline/${timelineItem.itemType}/${timelineItem.id}/moderation/`, {reason: reason}));
        }
        catch (error) {
            this.log.error(error.data || error);
        }
    }

    async createTopic(topic: Topic, teamId: string) { // Promise<TimelineItemCreation>
        try {
            const topicCreationDTO = new TopicCreationDTO(topic, teamId);
            const itemType = "topicItem";
            return await firstValueFrom(this.http.post(`/api/timeline/new/${itemType}/${teamId}`, topicCreationDTO));
        }
        catch (error) {
            this.log.error(error.data || error);
        }
    }

    async notifyAboutTopicCreation(topicId: string){
        try {
            return await firstValueFrom(this.http.post(`/api/timeline/topic/${topicId}/notify/`, {}));
        }
        catch (error) {
            this.log.error(error.data || error);
        }
    }

    async createKudos(kudos: Kudos, teamId: string){ // Promise<TimelineItemCreation>
        try {
            const kudosCreationDTO: KudosCreationDTO = new KudosCreationDTO(kudos, teamId);
            const itemType = "kudosItem";
            return await firstValueFrom(this.http.post(`/api/timeline/new/${itemType}/${teamId}`, kudosCreationDTO));
        }
        catch (error) {
            this.log.error(error.data || error);
        }
    }

    private isReactions(item: any): item is Reactions {
        return 'reactions' in item;
    }

    private isThreads(item: any): item is Threads {
        return 'threads' in item;
    }

    filterItems(timelineItems: TimelineItem[], filterOption: string): TimelineItem[] {
        let filteredTimelineItems: TimelineItem[] = timelineItems;
        switch (filterOption) {
            case 'all':
                break;
            case 'reviewed':
                filteredTimelineItems = timelineItems.filter(item => item.reviewed?.length > 0);
                break;
            case 'not_reviewed':
                filteredTimelineItems = timelineItems.filter(item => item.reviewed?.length === 0);
                break;
            case 'assigned':
                filteredTimelineItems = timelineItems.filter(item => item.managerPanel && item.managerPanel.assignees.length > 0);
                break;
            case 'not_assigned':
                filteredTimelineItems = timelineItems.filter(item => item.managerPanel && item.managerPanel.assignees.length === 0);
                break;
        }
        return filteredTimelineItems;
    }

    sortByCommentLength(timelineItems: TimelineItem[]) {
        const sortedTimelineItems: TimelineItem[] = sortBy(timelineItems, (item) => {
            let itemWeight: number;
            switch (item.itemType){
                case 'moodItem':
                    const mood = item as Mood;
                    itemWeight = mood.comment === null ? 1000 : 0;
                    break;
                case 'topicItem':
                    itemWeight = -3;
                    break;
                case 'kudosItem':
                    itemWeight = -5;
                    break;
                case 'tipsItem':
                    itemWeight = 10;
                    break;
                case 'badMoodStreakItem':
                    itemWeight = 0;
                    break;
                case 'badMoodPassItem':
                    itemWeight = 0;
                    break;
                case 'closedQuestionResultItem':
                    itemWeight = 0;
                    break;
                case 'agreeDisagreeQuestionResultItem':
                    itemWeight = 0;
                    break;
            }
            let reactionsCount = 0;
            if (this.isReactions(item) ) { // of Reactions type
                const agreeCount: number = item.reactions.filter(reaction => reaction.id === "agree")[0]?.count * -1 || 0;
                const disagreeCount: number = item.reactions.filter(reaction => reaction.id === "disagree")[0]?.count * -1 || 0;
                reactionsCount = (agreeCount + disagreeCount) * 2;
            }

            let threadsCount = 0;
            if (this.isThreads(item)) {
                threadsCount = item.threads? item.threads.length * -3 : 0;
            }

            itemWeight += reactionsCount;
            itemWeight += threadsCount;
            return itemWeight;
        })
        return sortedTimelineItems;
    }
}

export class KudosCreationDTO implements TaskActionContentDTO {
    id: "kudosCreation";
    message: string;
    teamId: string;
    recipients: KudosRecipients;
    kudosType: string;
    privacy: 'private' | 'public';

    constructor(kudos: Kudos, teamId: string) {
        this.id = "kudosCreation";
        this.message = kudos.message.body;
        this.teamId = teamId;
        this.recipients = kudos.recipients;
        this.kudosType = kudos.kudosType;
        this.privacy = kudos.privacy;
    }
}

class TopicCreationDTO {
    title: string;
    teamId: string;
    type: string;
    templateId: string;

    constructor(topic: Topic, teamId: string) {
        this.title = topic.title.body;
        this.teamId = teamId;
        this.type = topic.type;
        this.templateId = topic.templateId;
    }
}
